import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import type { Connection, PublicKey, TransactionInstruction } from '@solana/web3.js';
import BN from 'bn.js';
import { CpSwapValidationError } from '../accounts/cp-swap-validation.js';
import { getCpSwapProgram } from '../idl/loader.js';
import { deriveAmmAuthorityPda } from '../pda/cp-swap.js';
import { deriveEventAuthorityPda } from '../pda/derivations.js';
import { invokeMethod } from './common.js';

/** Immutable pool facts already obtained by the caller. */
export interface AmmSwapPoolAccounts {
  ammProgramId: PublicKey;
  poolState: PublicKey;
  ammConfig: PublicKey;
  token0Mint: PublicKey;
  token1Mint: PublicKey;
  token0Vault: PublicKey;
  token1Vault: PublicKey;
  token0Program: PublicKey;
  token1Program: PublicKey;
  observationKey: PublicKey;
}

/** Swap intent plus complete pool facts; building performs no RPC. */
export interface AmmSwapInstructionParams {
  pool: AmmSwapPoolAccounts;
  user: PublicKey;
  inputMint: PublicKey;
  amountIn: BN;
  minAmountOut: BN;
}

/** Encode the current cp-swap instruction, leaving funding, ATA creation and CU policy to callers. */
export async function buildAmmSwapInstruction(
  connection: Connection,
  params: AmmSwapInstructionParams,
): Promise<TransactionInstruction> {
  validateAmmSwapAmounts(params.amountIn, params.minAmountOut);
  const { pool } = params;
  const route = resolveAmmSwapRoute(pool, params.inputMint);
  const program = await getCpSwapProgram(connection, pool.ammProgramId);
  return invokeMethod(program, 'swapBaseInput', params.amountIn, params.minAmountOut)
    .accounts({
      payer: params.user,
      authority: deriveAmmAuthorityPda(pool.ammProgramId),
      ammConfig: pool.ammConfig,
      poolState: pool.poolState,
      inputTokenAccount: getAssociatedTokenAddressSync(
        route.inputMint,
        params.user,
        true,
        route.inputTokenProgram,
      ),
      outputTokenAccount: getAssociatedTokenAddressSync(
        route.outputMint,
        params.user,
        true,
        route.outputTokenProgram,
      ),
      inputVault: route.inputVault,
      outputVault: route.outputVault,
      inputTokenProgram: route.inputTokenProgram,
      outputTokenProgram: route.outputTokenProgram,
      inputTokenMint: params.inputMint,
      outputTokenMint: route.outputMint,
      observationState: pool.observationKey,
      eventAuthority: deriveEventAuthorityPda(pool.ammProgramId)[0],
      program: pool.ammProgramId,
    })
    .instruction();
}

/** Shared routing for the full transaction builder and the prehydrated instruction path. */
export function resolveAmmSwapRoute(
  pool: AmmSwapPoolAccounts,
  inputMint: PublicKey,
): {
  inputMint: PublicKey;
  outputMint: PublicKey;
  inputVault: PublicKey;
  outputVault: PublicKey;
  inputTokenProgram: PublicKey;
  outputTokenProgram: PublicKey;
} {
  const inputIsToken0 = inputMint.equals(pool.token0Mint);
  if (!inputIsToken0 && !inputMint.equals(pool.token1Mint)) {
    throw new CpSwapValidationError('mint_pair');
  }
  return {
    inputMint,
    outputMint: inputIsToken0 ? pool.token1Mint : pool.token0Mint,
    inputVault: inputIsToken0 ? pool.token0Vault : pool.token1Vault,
    outputVault: inputIsToken0 ? pool.token1Vault : pool.token0Vault,
    inputTokenProgram: inputIsToken0 ? pool.token0Program : pool.token1Program,
    outputTokenProgram: inputIsToken0 ? pool.token1Program : pool.token0Program,
  };
}

const U64_MAX: BN = new BN('18446744073709551615');

export function validateAmmSwapAmounts(amountIn: BN, minAmountOut: BN): void {
  if (amountIn.lten(0) || amountIn.gt(U64_MAX)) {
    throw new CpSwapValidationError('amount_in_out_of_range');
  }
  if (minAmountOut.isNeg() || minAmountOut.gt(U64_MAX)) {
    throw new CpSwapValidationError('min_amount_out_out_of_range');
  }
}
